import useSupportedLanguages from "@/hooks/useSupportedLanguages";
import {TranslationMethod} from "@/types/enums";
import {getLanguageName} from "@/utils/textFormatting";
import {useEffect, useMemo} from "react";
import Select from "@/Components/Select";
import {isDesktop} from "@/utils/deviceUtils";


export type LanguageWidgetProps = {
    selectedLanguage: string | string[];
    method: TranslationMethod;
    highlight?: boolean;
    multiple?: boolean;
} & (
    | {
    multiple: true;
    onSelect: (language: string[]) => void;
}
    | {
    multiple?: false;
    onSelect: (language: string) => void;
});

export default function LanguageWidget({
    onSelect,
    selectedLanguage,
    method,
    highlight = false,
    multiple = false
}: LanguageWidgetProps) {
    const [languageOptions, isLoadingLang] = useSupportedLanguages('translation', method);
    const customStyles = useMemo(() => (
        {
            control: (base: any) => {
                return {
                    ...base,
                    borderColor: highlight ? 'red' : base.borderColor,
                    boxShadow: highlight ? '0 0 0 1px red' : base.boxShadow,
                    '&:hover': {
                        borderColor: highlight ? 'red' : base.borderColor
                    }
                }
            }
        }
    ), [highlight]);

    const value = useMemo(() => {
        if (multiple) {
            const selectedArray = Array.isArray(selectedLanguage) ? selectedLanguage : [];
            return selectedArray.map(lang => ({
                label: getLanguageName(lang),
                value: lang
            }));
        } else {
            const selectedSingle = Array.isArray(selectedLanguage) ? selectedLanguage[0] : selectedLanguage;
            return selectedSingle ? {
                label: getLanguageName(selectedSingle),
                value: selectedSingle
            } : undefined;
        }
    }, [selectedLanguage, multiple]);

    const handleChange = (selectedOption: any) => {
        if (multiple) {
            const values = selectedOption ? selectedOption.map((option: any) => option.value) : [];
            (onSelect as (language: string[]) => void)(values);
        } else {
            const value = selectedOption ? selectedOption.value : '';
            (onSelect as (language: string) => void)(value);
        }
    };

    if(isLoadingLang) {
        return null;
    }

    return (
        <div className="goog-te-gadget" dir="ltr" style={{}}>
            <div id=":0.targetLanguage">
                <Select
                    variant="modal"
                    placeholder={multiple ? 'Select languages' : 'Select language'}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    menuPlacement="top"
                    styles={customStyles}
                    isClearable={false}
                    value={value}
                    inputId="lang-input"
                    isMulti={multiple}
                    onChange={handleChange}
                    options={languageOptions}
                    isSearchable={isDesktop()}
                />
            </div>
        </div>
    )
}
